import { FC, useCallback, useMemo, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useSoftSkillsQuery } from 'generated/graphql';
import IndicatorForm from './IndicatorForm';
import {
  emptyIndicator,
  IndicatorValues,
} from './EditIndicatorsDialog';

interface SoftSkillOption {
  id: number;
  name: string;
}

interface AddSoftSkillDialogProps {
  open: boolean;
  onClose: () => void;
  existingSoftSkillIds: number[];
  onAdd: (softSkillId: number, indicators: IndicatorValues[]) => void;
  loading?: boolean;
}

const AddSoftSkillDialog: FC<AddSoftSkillDialogProps> = ({
  open,
  onClose,
  existingSoftSkillIds,
  onAdd,
  loading,
}) => {
  const { data: softSkillsData, loading: softSkillsLoading } =
    useSoftSkillsQuery();

  const [selectedSkill, setSelectedSkill] = useState<SoftSkillOption | null>(
    null,
  );
  const [indicators, setIndicators] = useState<IndicatorValues[]>([]);

  const availableSkills: SoftSkillOption[] = useMemo(() => {
    const allSkills = softSkillsData?.SoftSkills ?? [];
    return allSkills
      .filter(s => !existingSoftSkillIds.includes(s.id))
      .map(s => ({ id: s.id, name: s.name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [softSkillsData, existingSoftSkillIds]);

  const handleIndicatorChange = useCallback(
    (indicatorIndex: number, updated: IndicatorValues) => {
      setIndicators(prev =>
        prev.map((ind, i) => (i === indicatorIndex ? updated : ind)),
      );
    },
    [],
  );

  const handleRemoveIndicator = useCallback((indicatorIndex: number) => {
    setIndicators(prev => prev.filter((_, i) => i !== indicatorIndex));
  }, []);

  const handleAddIndicator = useCallback(() => {
    setIndicators(prev => [...prev, emptyIndicator()]);
  }, []);

  const handleAdd = () => {
    if (!selectedSkill) return;
    onAdd(selectedSkill.id, indicators);
  };

  const handleClose = () => {
    setSelectedSkill(null);
    setIndicators([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth>
      <DialogTitle>Add Soft Skill to Question</DialogTitle>
      <DialogContent>
        <Autocomplete
          options={availableSkills}
          getOptionLabel={option => option.name}
          value={selectedSkill}
          onChange={(_, newValue) => setSelectedSkill(newValue)}
          loading={softSkillsLoading}
          renderInput={params => (
            <TextField
              {...params}
              label="Select Soft Skill"
              margin="normal"
              fullWidth
            />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
        />

        {indicators.map((ind, index) => (
          <IndicatorForm
            key={index}
            index={index}
            values={ind}
            onChange={updated => handleIndicatorChange(index, updated)}
            onRemove={() => handleRemoveIndicator(index)}
          />
        ))}

        <Box sx={{ mt: 1 }}>
          <Button
            startIcon={<AddIcon />}
            onClick={handleAddIndicator}
            size="small"
          >
            Add Indicator
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleAdd}
          variant="contained"
          disabled={loading || !selectedSkill}
        >
          {loading ? 'Adding...' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddSoftSkillDialog;
